import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useStore } from 'app/context';
import { AgenturModel } from 'app/models/agentur.model';
import { LeadNowBarChart } from 'app/components/berichte/leadnow/LeadNowBarChart';
import { LeadNowType, LeadNowVM } from 'app/stores/ui/agt.leadnow.ui.store';

interface IJpgLeadNowWidget {
	agt: AgenturModel;
	year: number;
}

/**
 * Widget to display LeadNow data for the last 12 months for a specific agency in JPG reports
 */
export const JpgLeadNowWidget = observer((props: IJpgLeadNowWidget) => {
	const { agtLeadNowStore } = useStore();
	const [items, setItems] = useState<LeadNowVM[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const loadLeadNowData = async () => {
			setLoading(true);
			try {
				// Get all available KWs
				const distinctKws = await agtLeadNowStore.getDistinctKWs();

				if (distinctKws.length === 0) {
					setLoading(false);
					return;
				}

				// Calculate KW range for last 12 months (approximately 52 weeks)
				const maxKw = distinctKws[distinctKws.length - 1];
				const maxKwYear = Math.floor(maxKw / 100);
				const maxKwWeek = maxKw % 100;

				// Calculate minKw: go back approximately 52 weeks
				let minKwYear = maxKwYear;
				let minKwWeek = maxKwWeek - 52;

				if (minKwWeek <= 0) {
					minKwYear -= 1;
					minKwWeek += 52; // Approximate weeks in a year
				}

				const minKw = minKwYear * 100 + minKwWeek;

				// Load aggregated data for the agency
				const data = await agtLeadNowStore.aggregateByKwRange(minKw, maxKw);

				// Filter for this specific agency
				const agtData = data.filter((d) => d.agtId === props.agt.agtId);

				// Create LeadNowVM instances
				const vms = agtData.map((d) => new LeadNowVM(props.agt, d));

				setItems(vms);
			} catch (error) {
				console.error('Error loading LeadNow data:', error);
			} finally {
				setLoading(false);
			}
		};

		loadLeadNowData();
	}, [props.agt.agtId, agtLeadNowStore]);

	if (loading) {
		return (
			<div className="pad-1rem color-grey">
				<p>Lade AMIS.NOW Leads Daten...</p>
			</div>
		);
	}

	if (items.length === 0) {
		return (
			<div className="pad-1rem color-grey">
				<p>Keine AMIS.NOW Leads Daten verfügbar</p>
			</div>
		);
	}

	return (
		<div>
			<LeadNowBarChart type={LeadNowType.brutto} items={items} />
		</div>
	);
});
